/**
 * The Timeline's arithmetic — a port of the generator's `renderTimeline`,
 * `workingDays` and `onLeave`, with the DOM taken out, plus the two Phase 7
 * additions: the Today line (design.md § 7.5) and leave on a bar of its own
 * (§ 7.8). The generator's `leaveSpans`, which hatched leave over a person's
 * schedule bar, is gone with it.
 *
 * Pure functions, no React: the view calls `buildTimeline` once per payload and
 * draws the result. The generator computed this while building elements and kept
 * `NON_WORKING` and `LEAVE` in module-level variables that the row code read; here
 * they are values passed down, so a redraw can never see a stale set, and `leave`
 * arrives beside `schedule` and `holidays` as one input (the generator's rebuild
 * dropped it once).
 *
 * Three deliberate differences from the generator, each recorded where it happens:
 *
 *   1. No built-in fallback plan. The generator shows a hardcoded insurance
 *      schedule, shutdown, contingency and target when a workbook has no Schedule
 *      sheet. In this application that would draw another client's room names
 *      under an unrelated blueprint, so `buildTimeline` returns `null` and the
 *      view says there is no schedule. The Meta-driven bands remain exactly the
 *      generator's mechanism.
 *
 *   2. Dates are compared as ISO strings and positioned in UTC. The generator
 *      mixed local `getDay()` with UTC `toISOString()`, and compared UTC-midnight
 *      dates against local-midnight snapshot times, so the same plan could show a
 *      different status in different time zones.
 *
 *   3. Bars are per PARTY, not per person (design.md § 7.7, T7.29). The
 *      generator drew one bar per Schedule row on a lane per person, so a room
 *      worked in three stints drew three bars. Here a room draws at most two:
 *      one Consultant, one Client, each merging that party's rows into a single
 *      span. `Person` stays free text (D-16) — it decides the party and still
 *      names the lead in the tooltip, but it no longer decides a lane.
 */
import type { RoomModel } from '@blueprint/shared';
import type { HolidayRow, LeaveRow, ScheduleRow } from '../../lib/blueprints';
import { metaDate, resolveScheduleItems, ymd, type ScheduleItem } from '../blueprint/weeks';

/**
 * The API's row id rides alongside the workbook fields, exactly as a
 * deliverable's does. A row parsed from an .xlsx has none, so it is optional,
 * and a bar containing such a row cannot be dragged.
 */
export type ScheduleRowWithId = ScheduleRow & { id?: string };

const MS = 86400000;

/** The generator's lane order and display names; anything else is shown as typed. */
const PERSON_ORDER = ['DE', 'BI'];
const PERSON_NAME: Record<string, string> = { DE: 'Data Engineer', BI: 'BI Developer' };

export function personName(person: string): string {
  return PERSON_NAME[person] ?? person;
}

/* ------------------------------------------------------- the two parties */

/**
 * Whose side of the room a Schedule row is (design.md § 7.7).
 *
 * The existing `Person` column is reused rather than a new one added, so no
 * workbook changes and every existing plan keeps working. `Client` is the
 * client's; `DE`, `BI`, blank and anything else is ours — the same default as
 * the deliverable `Owner` in § 7.6, so a plan cannot have a row on one side and
 * its deliverables on the other merely by leaving a cell empty.
 *
 * Not one of the nine overridable display terms: Consultant and Client are
 * parties to the engagement, not names for a Room, Pillar, Sliver, Deliverable
 * or a deliverable state.
 */
export type Party = 'consultant' | 'client';

/** Consultant above, Client below — the lane order is fixed on every row. */
export const PARTY_ORDER: readonly Party[] = ['consultant', 'client'];

export const PARTY_LABEL: Record<Party, string> = {
  consultant: 'Consultant',
  client: 'Client',
};

export function partyOfPerson(person: string): Party {
  return person.trim().toUpperCase() === 'CLIENT' ? 'client' : 'consultant';
}

export type Status = 'done' | 'wip' | 'plan';

export interface Band {
  start: string;
  end: string;
  label: string;
}

interface LeaveSpan {
  person: string;
  start: string;
  end: string;
  label: string;
}

const dayMs = (iso: string): number => Date.parse(iso + 'T00:00:00Z');
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** 'D Mon' in en-GB, computed in UTC so it cannot drift with the viewer's zone. */
export function fmtDay(iso: string): string {
  const d = new Date(dayMs(iso));
  return d.getUTCDate() + ' ' + d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
}

export function fmtDayYear(iso: string): string {
  return `${fmtDay(iso)} ${iso.slice(0, 4)}`;
}

function onLeave(leave: LeaveSpan[], person: string, iso: string): boolean {
  return leave.some((l) => l.person === person && iso >= l.start && iso <= l.end);
}

/** Working days between two ISO dates inclusive: weekdays, minus holidays and this person's leave. */
function workingDays(
  nonWorking: ReadonlySet<string>,
  leave: LeaveSpan[],
  a: string,
  b: string,
  person: string,
): number {
  let n = 0;
  for (let t = dayMs(a), end = dayMs(b); t <= end; t += MS) {
    const dow = new Date(t).getUTCDay();
    const iso = isoOf(t);
    if (dow !== 0 && dow !== 6 && !nonWorking.has(iso) && !(person && onLeave(leave, person, iso))) n++;
  }
  return n;
}

/**
 * One person's leave, drawn on a bar of its own (design.md § 7.8).
 *
 * `start` and `end` are clipped to the rendered range so the bar cannot run off
 * the track; `dateLabel` and `title` carry the **real** dates from the sheet,
 * because the hover has to answer "between which dates is this person away".
 *
 * Nothing is derived from a leave bar: it shortens, shifts and annotates nothing.
 * Werner expresses the impact of leave by moving the schedule dates himself.
 */
export interface LeaveBar {
  person: string;
  /** The person as the Leave sheet names them, resolved through `personName`. */
  personLabel: string;
  label: string;
  start: string;
  end: string;
  /** A lane per person, so two people away at once do not draw over each other. */
  lane: number;
  left: number;
  width: number;
  title: string;
}

/**
 * One underlying Schedule row behind a bar.
 *
 * A bar is a RENDERING of one or more rows, which is why the drag edit patches
 * these individually rather than sending a delta for "the bar" — the API has no
 * notion of a bar, and nothing is recalculated from a date change anyway.
 */
export interface BarRow {
  /** The database id, absent for rows parsed from a workbook. */
  id: string | undefined;
  start: string;
  end: string;
  person: string;
  pillarName: string;
  lead: string;
}

/** A resolved schedule item beside the id of the row it came from. */
interface Paired {
  item: ScheduleItem;
  id: string | undefined;
}

export interface TimelineBar {
  party: Party;
  /** 0 Consultant, 1 Client — fixed, so the position reads the same on every row. */
  lane: number;
  /** The merged span: `min(StartDate)` to `max(EndDate)` across `rows`. */
  start: string;
  end: string;
  left: number;
  width: number;
  title: string;
  /** The rows this bar merges, earliest start first. */
  rows: BarRow[];
  /** Working days summed over the rows, so a hidden gap is not counted as work. */
  days: number;
  /** Every row has an id, so a drag can address all of them. */
  addressable: boolean;
}

export interface TimelineRow {
  roomID: string;
  /** "R3 Policy Administration" — the room, never a pillar: bars are per room now. */
  name: string;
  status: Status;
  /** Deliverables in this room, for the row's caption. */
  count: number;
  /** The Leads named on this room's rows, de-duplicated. */
  who: string;
  title: string;
  dateLabel: string;
  dateStyle: { left: string } | { right: string };
  bars: TimelineBar[];
}

export interface TimelineLayout {
  title: string;
  snapshot: string | null;
  roomCount: number;
  range: string;
  /** The parties actually drawn, in lane order, for the legend. */
  parties: Party[];
  /** Days across the rendered range, so a pixel drag converts to whole days. */
  rangeDayCount: number;
  months: { key: string; width: number; label: string }[];
  monthEdges: number[];
  rows: TimelineRow[];
  shutdown: (Band & { left: number; width: number }) | null;
  contingency: (Band & { left: number; width: number }) | null;
  holidays: { left: number; title: string }[];
  target: { left: number; title: string; iso: string } | null;
  /**
   * The real current date, drawn only when it falls inside the rendered range
   * (design.md § 7.5). Never `Meta.SnapshotDate`: the snapshot is what the plan
   * was assessed against, and a plan assessed last month is still read today.
   */
  today: {
    iso: string;
    left: number;
    title: string;
    /** The line sits near the right edge, so its caption is set to its left. */
    flip: boolean;
  } | null;
  /** The leave row: one bar per Leave sheet row that overlaps the range. */
  leaveBars: LeaveBar[];
  leaveLaneCount: number;
  /** The people on the leave row, for its caption. */
  leaveWho: string;
  /** Every leave span in full, for the row label's tooltip. */
  leaveTitle: string;
  hasLeave: boolean;
  hasHolidays: boolean;
}

export interface TimelineInput {
  meta: Record<string, unknown>;
  rooms: RoomModel[];
  schedule: ScheduleRowWithId[];
  holidays: HolidayRow[];
  leave: LeaveRow[];
  /** Today: the Today line and the status fallback. Injectable so a test can pin both. */
  today?: Date;
}

/** Status is derived from the snapshot, never stored, so it stays right as dates move. */
export function statusOf(start: string, end: string, on: string): Status {
  if (end < on) return 'done';
  if (start <= on && on <= end) return 'wip';
  return 'plan';
}

export function buildTimeline(input: TimelineInput): TimelineLayout | null {
  const { meta, rooms } = input;
  const items = resolveScheduleItems(input.schedule, rooms);
  if (!items.length) return null;

  /* Each item paired with the Schedule row it came from, so a drag can address
     that row by id. `resolveScheduleItems` keeps the rows in order and drops
     only those missing a date, so the kept rows line up one for one. That is
     CHECKED rather than assumed: if the two ever stop lining up the ids go
     missing and the bars become undraggable — visible — instead of a drag
     silently moving somebody else's dates. */
  const keptRows = input.schedule.filter((r) => Boolean(metaDate(r.StartDate) && metaDate(r.EndDate)));
  const aligned = keptRows.length === items.length;
  const paired = items.map((item, k) => ({ item, id: aligned ? keptRows[k]?.id : undefined }));

  const holidays = input.holidays
    .map((h) => ({ d: metaDate(h.Date), label: h.Label || 'Holiday' }))
    .filter((h): h is { d: string; label: string } => Boolean(h.d));

  const band = (s: unknown, e: unknown, label: string): Band | null => {
    if (!s || !e) return null;
    const start = metaDate(s);
    const end = metaDate(e);
    return start && end ? { start, end, label } : null;
  };
  const shutdown = band(
    meta.ShutdownStart,
    meta.ShutdownEnd,
    meta.ShutdownLabel ? String(meta.ShutdownLabel) : 'Shutdown',
  );
  const contingency = band(meta.ContingencyStart, meta.ContingencyEnd, 'Contingency');
  const target = meta.TargetFinish ? metaDate(meta.TargetFinish) : null;

  // The range: an explicit Meta range wins, otherwise every date we have with a
  // little padding, so the first and last bars are not flush with the edge.
  const times: number[] = [];
  for (const i of items) times.push(dayMs(i.start), dayMs(i.end));
  if (shutdown) times.push(dayMs(shutdown.start), dayMs(shutdown.end));
  if (contingency) times.push(dayMs(contingency.start), dayMs(contingency.end));
  if (target) times.push(dayMs(target));
  const range = {
    start: metaDate(meta.TimelineRangeStart) ?? isoOf(Math.min(...times) - 7 * MS),
    end: metaDate(meta.TimelineRangeEnd) ?? isoOf(Math.max(...times) + 2 * MS),
  };

  // Snapshot date if the plan has one, otherwise today.
  const snapshot = meta.SnapshotDate ? metaDate(meta.SnapshotDate) : null;
  const on = snapshot ?? ymd(input.today ?? new Date());

  const t0 = dayMs(range.start);
  const t1 = dayMs(range.end) + MS;
  const pct = (t: number) => ((t - t0) / (t1 - t0)) * 100;
  const span = (s: string, e: string): [number, number] => {
    const a = pct(dayMs(s));
    const b = pct(dayMs(e) + MS);
    return [a, b - a];
  };

  /* Month header */
  const months: TimelineLayout['months'] = [];
  const monthEdges: number[] = [];
  for (let m = t0; m < t1; ) {
    const d = new Date(m);
    const next = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    if (m > t0) monthEdges.push(pct(m));
    months.push({
      key: isoOf(m),
      width: ((Math.min(next, t1) - Math.max(m, t0)) / (t1 - t0)) * 100,
      label: d.toLocaleString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
    });
    m = next;
  }

  // Holidays and the shutdown block are excluded from every person's day count.
  const nonWorking = new Set(holidays.map((h) => h.d));
  if (shutdown) for (let t = dayMs(shutdown.start), e = dayMs(shutdown.end); t <= e; t += MS) nonWorking.add(isoOf(t));

  const leave: LeaveSpan[] = input.leave
    .map((l) => ({
      person: String(l.Person ?? '').trim().toUpperCase(),
      start: metaDate(l.StartDate),
      end: metaDate(l.EndDate),
      label: l.Label || 'Leave',
    }))
    .filter((l): l is LeaveSpan => Boolean(l.start && l.end));

  /* Deliverable counts per room, for the row captions. A bar is per room now, so
     the per-pillar count the generator used for pillar-scoped rows has no reader. */
  const countByRoom = new Map<string, number>();
  for (const rm of rooms) {
    let n = 0;
    for (const pl of rm.pillars ?? []) n += (pl.groups ?? []).reduce((s, g) => s + g.deliverables.length, 0);
    countByRoom.set(String(rm.RoomID), n);
  }

  /* One row per room, in the order the rooms first appear in the Schedule sheet,
     and at most two bars on it (§ 7.7, T7.29). A room is one row whether its
     work is one stint or six. */
  const byRoom = new Map<string, Paired[]>();
  const groups: Paired[][] = [];
  for (const p of paired) {
    let g = byRoom.get(p.item.roomID);
    if (!g) {
      g = [];
      byRoom.set(p.item.roomID, g);
      groups.push(g);
    }
    g.push(p);
  }

  const roomLabel = new Map(rooms.map((r) => [String(r.RoomID), `${r.RoomID} ${r.RoomName}`]));

  const rowsOut: TimelineRow[] = groups.map((grp) => {
    const head = (grp[0] as Paired).item;
    const name = roomLabel.get(head.roomID) ?? head.roomID;
    const stat = grp.map((p) => statusOf(p.item.start, p.item.end, on));
    const status: Status = stat.some((s) => s === 'wip')
      ? 'wip'
      : stat.every((s) => s === 'done')
        ? 'done'
        : 'plan';

    const count = countByRoom.get(head.roomID) ?? 0;

    const roomStart = grp.reduce((a, p) => (p.item.start < a ? p.item.start : a), head.start);
    const roomEnd = grp.reduce((a, p) => (p.item.end > a ? p.item.end : a), head.end);
    const [rl, rw] = span(roomStart, roomEnd);

    /* The merge. Every row of one party in one room becomes a single span from
       its earliest start to its latest end. This HIDES GAPS — two stints a
       month apart draw as one continuous bar — and that is deliberate
       (design.md § 7.7): the bar answers "when is this room this side's
       problem", and the deliverable list behind it carries the detail. The
       tooltip still names each underlying row, so the gap is readable, not
       concealed; the working-day count sums the rows, so it never counts a gap
       as work. */
    const bars: TimelineBar[] = PARTY_ORDER.flatMap((party, lane) => {
      const mine = grp
        .filter((p) => partyOfPerson(p.item.person) === party)
        .sort((a, b) => (a.item.start < b.item.start ? -1 : a.item.start > b.item.start ? 1 : 0));
      const first = mine[0];
      if (!first) return [];

      const start = mine.reduce((a, p) => (p.item.start < a ? p.item.start : a), first.item.start);
      const end = mine.reduce((a, p) => (p.item.end > a ? p.item.end : a), first.item.end);
      const days = mine.reduce(
        (s, p) => s + workingDays(nonWorking, leave, p.item.start, p.item.end, p.item.person),
        0,
      );
      const [left, width] = span(start, end);

      const leads = [...new Set(mine.map((p) => p.item.who).filter(Boolean))].join(', ');
      const people = [...new Set(mine.map((p) => p.item.person).filter(Boolean))]
        .map(personName)
        .join(', ');
      const merged =
        mine.length > 1
          ? '\n' +
            `${mine.length} schedule rows, merged: ` +
            mine.map((p) => `${fmtDay(p.item.start)} – ${fmtDay(p.item.end)}`).join(', ')
          : '';

      const barRows: BarRow[] = mine.map((p) => ({
        id: p.id,
        start: p.item.start,
        end: p.item.end,
        person: p.item.person,
        pillarName: p.item.pillarName,
        lead: p.item.who,
      }));

      return [
        {
          party,
          lane,
          start,
          end,
          left,
          width,
          rows: barRows,
          days,
          addressable: barRows.every((r) => Boolean(r.id)),
          title:
            `${PARTY_LABEL[party]} — ${name}: ${fmtDay(start)} – ${fmtDay(end)} ` +
            // T7.44. `days` SUMS the constituent rows, so for a single row it is
            // that person's working days, but for several it is person-days —
            // two people working the same three weeks come to 27 over a
            // 16-working-day span. Summing is deliberate (a hidden gap must not
            // count as work), so the label changes rather than the arithmetic:
            // calling person-days "working days" is the only part that was wrong.
            `(${days} ${mine.length > 1 ? `person-days across ${mine.length} schedule rows` : 'working days'})` +
            (people ? `\n${people}` : '') +
            (leads ? `\nLead: ${leads}` : '') +
            merged,
        },
      ];
    });

    return {
      roomID: head.roomID,
      name,
      status,
      count,
      who: [...new Set(grp.map((p) => p.item.who).filter(Boolean))].join(', '),
      // Leave is no longer annotated here: it has a row of its own (§ 7.8).
      title: bars.map((b) => b.title).join('\n\n'),
      dateLabel: `${fmtDay(roomStart)} – ${fmtDay(roomEnd)}`,
      // Late rows put their caption to the left of the bar's end instead of overflowing.
      dateStyle: rl > 72 ? { right: `${100 - rl - rw}%` } : { left: `${rl}%` },
      bars,
    };
  });

  /* The leave row keeps a lane per person, so it still needs the person order
     the schedule rows imply — DE and BI first, as the generator had them. */
  const seen: string[] = [];
  for (const i of items) if (i.person && !seen.includes(i.person)) seen.push(i.person);
  const laneOrder = [...PERSON_ORDER, ...seen.filter((p) => !PERSON_ORDER.includes(p))];

  /* The leave row (§ 7.8). One lane per person, in the same order as the
     schedule lanes, so a person's leave sits where their bars do. Spans outside
     the rendered range are dropped; a span that straddles an edge is clipped for
     drawing only — the caption and the tooltip keep the sheet's real dates. */
  const leavePersons: string[] = [];
  for (const l of leave) if (!leavePersons.includes(l.person)) leavePersons.push(l.person);
  const leaveOrder = [
    ...laneOrder.filter((p) => leavePersons.includes(p)),
    ...leavePersons.filter((p) => !laneOrder.includes(p)),
  ];
  const leaveBars: LeaveBar[] = leave
    .filter((l) => l.start <= range.end && l.end >= range.start)
    .map((l) => {
      const from = l.start > range.start ? l.start : range.start;
      const to = l.end < range.end ? l.end : range.end;
      const [left, width] = span(from, to);
      return {
        person: l.person,
        personLabel: personName(l.person),
        label: l.label,
        start: l.start,
        end: l.end,
        lane: Math.max(0, leaveOrder.indexOf(l.person)),
        left,
        width,
        title: `${personName(l.person)} — ${l.label}: ${fmtDay(l.start)} – ${fmtDay(l.end)}`,
      };
    });

  /* The Today line (§ 7.5): the real current date, never the snapshot. */
  const todayIso = ymd(input.today ?? new Date());
  const today =
    todayIso >= range.start && todayIso <= range.end
      ? {
          iso: todayIso,
          left: pct(dayMs(todayIso) + MS / 2),
          title: `Today — ${fmtDayYear(todayIso)}`,
          flip: pct(dayMs(todayIso) + MS / 2) > 88,
        }
      : null;

  const bandOut = (b: Band | null) => {
    if (!b) return null;
    const [left, width] = span(b.start, b.end);
    return { ...b, left, width };
  };

  const bounds = items.reduce(
    (a, i) => ({ s: !a.s || i.start < a.s ? i.start : a.s, e: !a.e || i.end > a.e ? i.end : a.e }),
    { s: '', e: '' },
  );

  return {
    title: typeof meta.Title === 'string' ? meta.Title : '',
    snapshot,
    roomCount: rooms.length,
    range: bounds.s && bounds.e ? `${fmtDayYear(bounds.s)} → ${fmtDayYear(bounds.e)}` : '',
    parties: PARTY_ORDER.filter((p) => rowsOut.some((r) => r.bars.some((b) => b.party === p))),
    rangeDayCount: Math.round((t1 - t0) / MS),
    months,
    monthEdges,
    rows: rowsOut,
    shutdown: bandOut(shutdown),
    contingency: bandOut(contingency),
    holidays: holidays.map((h) => ({
      left: pct(dayMs(h.d) + MS / 2),
      title: `${h.label} (${h.d})`,
    })),
    target: target
      ? { left: pct(dayMs(target) + MS), iso: target, title: `Target finish: ${fmtDayYear(target)}` }
      : null,
    today,
    leaveBars,
    leaveLaneCount: leaveBars.length ? Math.max(...leaveBars.map((l) => l.lane + 1)) : 0,
    // Both describe what is drawn, so a span outside the range is in neither.
    leaveWho: leaveOrder.filter((p) => leaveBars.some((l) => l.person === p)).map(personName).join(', '),
    leaveTitle: leaveBars.map((l) => l.title).join('\n'),
    hasLeave: leaveBars.length > 0,
    hasHolidays: holidays.length > 0,
  };
}
